import { useEffect, useState } from 'react';

export interface YoutubeOEmbedMetadata {
  title: string;
  /** Empty when oEmbed omits the uploader. */
  channel: string;
}

interface YoutubeOEmbedResponse {
  title?: unknown;
  author_name?: unknown;
}

const metadataCache = new Map<string, YoutubeOEmbedMetadata>();
const requestCache = new Map<string, Promise<YoutubeOEmbedMetadata>>();

/**
 * oEmbed only ever hands back the 4:3 `hqdefault` still, which bakes black bars
 * into 16:9 videos, so thumbnails are built from the video id instead.
 */
export const youtubeThumbnailUrl = (videoId: string) =>
  `https://i.ytimg.com/vi/${encodeURIComponent(videoId)}/maxresdefault.jpg`;

/** Always present, unlike `maxresdefault`, and 16:9 like it. */
export const youtubeFallbackThumbnailUrl = (videoId: string) =>
  `https://i.ytimg.com/vi/${encodeURIComponent(videoId)}/mqdefault.jpg`;

const getYoutubeOEmbedMetadata = (videoId: string) => {
  const cached = metadataCache.get(videoId);
  if (cached) return Promise.resolve(cached);

  const pending = requestCache.get(videoId);
  if (pending) return pending;

  const videoUrl = new URL('https://www.youtube.com/watch');
  videoUrl.searchParams.set('v', videoId);

  const oEmbedUrl = new URL('https://www.youtube.com/oembed');
  oEmbedUrl.searchParams.set('url', videoUrl.toString());
  oEmbedUrl.searchParams.set('format', 'json');

  const request = fetch(oEmbedUrl, { headers: { Accept: 'application/json' } })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`YouTube oEmbed returned ${response.status}.`);
      }

      const payload = await response.json() as YoutubeOEmbedResponse;
      if (typeof payload.title !== 'string') {
        throw new Error('YouTube oEmbed returned incomplete metadata.');
      }

      const metadata = {
        title: payload.title,
        channel: typeof payload.author_name === 'string' ? payload.author_name : '',
      };
      metadataCache.set(videoId, metadata);
      return metadata;
    })
    .finally(() => requestCache.delete(videoId));

  requestCache.set(videoId, request);
  return request;
};

export function useYoutubeOEmbed(videoId?: string) {
  const [metadata, setMetadata] = useState<YoutubeOEmbedMetadata | null>(
    () => videoId ? metadataCache.get(videoId) ?? null : null
  );
  const [loading, setLoading] = useState(() => Boolean(videoId && !metadataCache.has(videoId)));

  useEffect(() => {
    if (!videoId) {
      setMetadata(null);
      setLoading(false);
      return;
    }

    const cached = metadataCache.get(videoId);
    if (cached) {
      setMetadata(cached);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setMetadata(null);
    setLoading(true);

    getYoutubeOEmbedMetadata(videoId)
      .then((loadedMetadata) => {
        if (!cancelled) setMetadata(loadedMetadata);
      })
      .catch(() => {
        if (!cancelled) setMetadata(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [videoId]);

  return { metadata, loading };
}
